import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

import { SignupHeader } from "components/atoms/signup-header/index";
import { SignupFooter } from "components/atoms/signup-footer/index";
import { SignupList } from "components/atoms/signup-list/index";
import { Button } from "components/atoms/button/index";
import NotificationPage from "components/molecules/notification-page";
import { Loader } from "components/atoms/loader";
import { CloseButton } from "components/atoms/signup-closebutton/index";
import * as S from "./signup.styles";
import { ReactComponent as TickIcon } from "assets/images/tick.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/exclamation.svg";
import {
  BaseFields,
  PasswordField,
  redirectDomains,
  BUTTON_PROPS,
  SignupResponseMessage,
} from "./constants";
import { UserService } from "core/service/user.service";
import { AuthService } from "core/service/auth.service";
import { Config } from "core/service/config.service";
import { Field } from "components/molecules/Field";
import { PasswordStrengthMeter } from "components/atoms/password-strength-meter/index";
import { PartnerSignUp } from "components/organisms/which-signup/index";
import { UtilService } from "core/utils/utils";
import ReCAPTCHA from "react-google-recaptcha";
import ActivationCodeSentImage from "assets/images/activation-sent.png";

// Update Field component to use React.forwardRef
const ForwardedField = React.forwardRef((props, ref) => {
  return <Field {...props} forwardedRef={ref} />;
});

export const SignUp = () => {
  const { t } = useTranslation();
  const refCaptcha = useRef();
  const siteKey = Config.getCaptchaSiteKey();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm({ mode: "all", defaultValues: { email_consent: true } });
  const [PopupResponse, setPopupResponse] = useState("");
  const [responseMessage, setResponseMessage] = useState("");

  useEffect(() => {
    if (!PopupResponse) return;
    setResponseMessage(
      PopupResponse === "error"
        ? SignupResponseMessage.error
        : SignupResponseMessage.success
    );
    if (PopupResponse === "success") {
      const timer = setTimeout(() => {
        setPopupResponse("");
      }, 8000);

      return () => clearTimeout(timer);
    }
  }, [PopupResponse]);

  const [showText, setShowText] = useState(false);
  const [affiliate, setAffiliate] = useState(false);
  const [refsite, setRefsite] = useState(false);
  const [isNewSignupEnabled, setIsNewSignupEnabled] = useState(false);
  const password = useRef({});
  const [activationLinkSentStatus, setActivationLinkSentStatus] =
    useState(false);
  const [Fields, setFields] = useState(BaseFields);
  password.current = watch("password", "");

  useEffect(() => {
    const affiliate = AuthService.getAffiliateDetails();
    const refsite = UtilService.getCookie("gst_ref_test");
    setAffiliate(affiliate);
    setRefsite(refsite);
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await UserService.getSignupConfig();
        if (response?.data) {
          setIsNewSignupEnabled(response.data.enableNewSignup);
          if (response.data.enablePhoneNumber) {
            setFields(BaseFields);
          } else {
            setFields(
              BaseFields.filter((field) => field.name !== "phone_number")
            );
          }
        }
      } catch (error) {
        setIsNewSignupEnabled(false);
      }
    };

    fetchConfig();
  }, []);

  const onSubmit = async (values) => {
    try {
      const captchaToken = await refCaptcha.current.executeAsync();
      const signupValues = {
        ...values,
        token: captchaToken,
        affiliate: affiliate.aff_id,
        refsite: refsite,
        affiliateName: affiliate.aff_name,
        send_email: isNewSignupEnabled ? Boolean(values.email_consent) : false, // GST-478 Added GDPR email consent for the HTML update
      };
      setPopupResponse("loading");
      const res = await UserService.signup(signupValues);

      if (typeof res === "boolean") {
        setPopupResponse("success");
        setActivationLinkSentStatus(true);
      } else if (res && Object.keys(res.payload).length) {
        setPopupResponse("success");
        setTimeout(() => {
          redirectToSimpletax();
        }, 1500);
      } else {
        setPopupResponse("error");
        refCaptcha.current.reset();
      }
    } catch (error) {
      console.error("Error during signup:", error);
      refCaptcha.current.reset();
    }
  };

  const redirectToSimpletax = () => {
    const redirectUrl = Config.getRedirectUrl();
    const redirectTo = redirectDomains[affiliate["name"]]
      ? redirectDomains[affiliate["name"]]
      : redirectUrl;
    window.location.replace(`${redirectTo}`);
  };

  return (
    <>
      {affiliate.signup ? (
        <PartnerSignUp />
      ) : (
        <S.Container>
          <SignupHeader company={affiliate} />
          <S.SignUpSection>
            <SignupList />
            <S.ContainerRight>
              {activationLinkSentStatus ? (
                <NotificationPage
                  header={"activationCode.sentHeader"}
                  body={"activationCode.sentText"}
                  image={ActivationCodeSentImage}
                  footer={"activationCode.sentFooter"}
                  buttonProps={BUTTON_PROPS}
                />
              ) : (
                <>
                  {["success", "error"].includes(PopupResponse) && (
                    <S.Overlay>
                      <S.Response type={PopupResponse}>
                        <S.ResponseContent>
                          <S.AlertIcon state={PopupResponse}>
                            {PopupResponse === "error" ? (
                              <ExclamationIcon />
                            ) : (
                              <TickIcon />
                            )}
                          </S.AlertIcon>
                          <S.ResponseMessage>
                            {responseMessage}
                          </S.ResponseMessage>
                          <CloseButton
                            state={PopupResponse}
                            onClick={() => setPopupResponse("")}
                          ></CloseButton>
                        </S.ResponseContent>
                      </S.Response>
                    </S.Overlay>
                  )}
                  <S.SignupForm onSubmit={handleSubmit(onSubmit)}>
                    {PopupResponse === "loading" && <Loader />}
                    <S.DiscountTitle>
                      {t("signup.sub_header_right")}
                    </S.DiscountTitle>
                    {Fields.map((obj, index) => (
                      <Field
                        labelcolor="light"
                        font="small"
                        top="top"
                        {...obj}
                        register={register}
                        error={errors[obj.name]?.message}
                        key={index}
                        classname=""
                      />
                    ))}
                    {PasswordField.map((obj, index) => (
                      <S.PasswordWrapper key={index}>
                        <ForwardedField
                          ref={password}
                          type={UtilService.setPasswordType(
                            PasswordField,
                            showText
                          )}
                          labelcolor="light"
                          font="small"
                          top="top"
                          {...obj}
                          register={register}
                          error={errors[obj.name]?.message}
                          key={index}
                        />
                        <S.EyeContainer onClick={() => setShowText(!showText)}>
                          {showText ? <S.BsEyeFill /> : <S.BsEyeSlashFill />}
                        </S.EyeContainer>
                        <S.PasswordInfo>
                          {t("signup.password_validation")}
                        </S.PasswordInfo>
                      </S.PasswordWrapper>
                    ))}
                    {password.current && (
                      <PasswordStrengthMeter value={password.current} />
                    )}
                    {isNewSignupEnabled && (
                      <S.CheckboxContainer>
                        <input
                          type="checkbox"
                          id="email_consent"
                          {...register("email_consent")}
                          defaultChecked={true}
                        />
                        <label htmlFor="email_consent">
                          {t("signup.email_consent.label")}
                        </label>
                      </S.CheckboxContainer>
                    )}
                    <S.ButtonClass>
                      <p>
                        {t("signup.terms.message")}
                        <Link to="/redirect-to-simpletax-terms">
                          {t("signup.terms.link")}
                        </Link>
                      </p>
                      <ReCAPTCHA
                        ref={refCaptcha}
                        size="invisible"
                        sitekey={siteKey}
                      />
                      <S.ButtonContainer>
                        <Button
                          value={t("signup.button")}
                          disabled={!isValid}
                          font="16px"
                          weight="500"
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                          }}
                        />
                      </S.ButtonContainer>
                    </S.ButtonClass>
                    <S.AlreadyRegistered>
                      {t("signup.existing_user.message")}
                      <Link to="/signin">
                        {" "}
                        {t("signup.existing_user.signin")}
                      </Link>
                    </S.AlreadyRegistered>
                  </S.SignupForm>
                </>
              )}
            </S.ContainerRight>
          </S.SignUpSection>
          <SignupFooter />
        </S.Container>
      )}
    </>
  );
};
